import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_MARKS } from "../brand/marks";
import { ALL_PRODUCTS } from "../nav/navigation";
import type { CtaLink, ServicePageContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * Warden service template: the problem the engagement addresses → how it runs →
 * what you hold at the end → the instruments in play → the products involved.
 *
 * Deliverables come before products deliberately. A service is bought on what
 * arrives at the end of it, and leading with the product would make this read
 * as a license sale with consulting attached.
 */
export function ServicePage({ content }: { content: ServicePageContent }) {
  useEffect(() => {
    document.title = `${content.title} — Warden by Odingard`;
  }, [content.title]);

  const products = (content.products?.slugs ?? [])
    .map((slug) => ALL_PRODUCTS.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  return (
    <main className="page pdp service" id="main">
      <section className="pdp__hero">
        <p className="page__eyebrow">Warden · {content.practice}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
        <div className="pdp__ctas">
          {content.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>

      <section className="pdp__section" id="scope">
        <h2>{content.scope.heading}</h2>
        <p className="pdp__body">{content.scope.body}</p>
        <ol className="pdp__steps">
          {content.scope.points.map((point) => (
            <li key={point.title}>
              <b>{point.title}</b>
              <span>{point.body}</span>
            </li>
          ))}
        </ol>
      </section>

      {content.method && (
        <section className="pdp__section" id="method">
          <h2>{content.method.heading}</h2>
          {content.method.lede && <p className="pdp__body">{content.method.lede}</p>}
          <div className="pdp__features">
            {content.method.steps.map((step, i) => (
              <article key={step.title}>
                <span className="service__step">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="pdp__section pdp__section--evidence" id="deliverables">
        <h2>{content.deliverables.heading}</h2>
        {content.deliverables.lede && (
          <p className="pdp__body">{content.deliverables.lede}</p>
        )}
        <ul className="service__deliverables">
          {content.deliverables.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {content.drivers && (
        <section className="pdp__section" id="drivers">
          <h2>{content.drivers.heading}</h2>
          {content.drivers.lede && <p className="pdp__body">{content.drivers.lede}</p>}
          <dl className="outcome__drivers">
            {content.drivers.items.map((item) => (
              <div key={item.title}>
                <dt>{item.title}</dt>
                <dd>{item.body}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {content.products && products.length > 0 && (
        <section className="pdp__section" id="products">
          <h2>{content.products.heading}</h2>
          {content.products.lede && <p className="pdp__body">{content.products.lede}</p>}
          <div className="home__cards">
            {products.map((p) => {
              const Mark = PRODUCT_MARKS[p.mark];
              return (
                <Link key={p.slug} to={p.href} className="mm__card">
                  <span className="mm__lockup" style={{ color: p.accent }}>
                    <Mark size={30} />
                    <b>{p.name}</b>
                  </span>
                  <span className="mm__descriptor">{p.descriptor}</span>
                  <span className="mm__blurb">{p.blurb}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {content.related && (
        <section className="pdp__section" id="related">
          <h2>{content.related.heading}</h2>
          <ul className="pdp__links">
            {content.related.links.map((link) => (
              <li key={link.href}>
                <Link to={link.href}>{link.label} →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
